import { createApp } from "vue";
import createAppRouter from "./pages/AppRouter.js";
import App from "./pages/App.vue";
import createAppStore from "./pages/AppStore.js";
import COMMON_UTIL from "../resources/js/common.js";
import filters from './common/filters.js';
import cmmnPlugin from './common/commonPlugin.js';
import VueCookies from "vue-cookies";
import SvgIcon from "@jamescoyle/vue-icon";
import VueDaumPostcode from "vue-daum-postcode";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css'
import '../resources/scss/main.scss';
import { getCntxtPth } from "../resources/api/cntxtPth.js";
import { getStorageMode, getLoginMode } from "../resources/api/loginPolicy.js";
import { findDefaultThema, findMainThema } from "../resources/api/thema.js";
import { setGlobalRouter } from "./pages/AppStore.js";

// 전역 테마 정보 (메모리에만 존재)
let globalThemeInfo = null;

// 테마 API 응답 캐시
let themeApiCache = {
	defaultTheme: null,
	mainTheme: null,
	fetchedAt: null
};

let themeCssEnabled = true;

// 사용자 테마 CSS 켜기/끄기 (App.vue 가 라우트마다 호출)
export function setThemeCssEnabled(enabled) {
	themeCssEnabled = enabled;
	document.querySelectorAll('link[data-theme-resource="css"]').forEach(link => {
		link.disabled = !enabled;
	});
}

// 테마 CSS link 를 붙이면서 현재 적용 여부를 반영
function appendThemeCssLink(link) {
	document.head.appendChild(link);
	link.disabled = !themeCssEnabled;
}

// 테마 정보 getter
export function getThemeInfo() {
	return globalThemeInfo;
}

// 테마 API 캐시 getter (AppRouter.js에서 사용)
export function getThemeApiCache() {
	return themeApiCache;
}

// 테마 버전만 체크 (변경되면 새로고침)
export async function checkAndReloadTheme() {
	try {
		const res = await findMainThema();
		if (res.status === 200 && res.data.data.thema) {
			const newTheme = res.data.data.thema;
			const newVersion = newTheme.mdfcnDt || newTheme.regDt;
			const currentVersion = globalThemeInfo?.mainTheme?.mdfcnDt || globalThemeInfo?.mainTheme?.regDt;

			// 버전이 다르면 페이지 새로고침
			if (currentVersion && newVersion && newVersion !== currentVersion) {
				// console.log('테마 변경 감지:', currentVersion, '->', newVersion);
				window.location.reload();
				return true;
			}
		}
		return false;
	} catch (error) {
		console.error('테마 버전 체크 실패:', error);
		return false;
	}
}

// CSS 동적 로드
function loadThemeCSS(mainTheme, defaultTheme) {
	// 기본 테마의 CSS 파일
	const defaultThemeFiles = defaultTheme?.themaFiles || [];
	const defaultCssFiles = defaultThemeFiles.filter(f => f.themaCl === 'css');

	// 메인 테마의 모든 CSS 파일
	const mainThemeFiles = mainTheme?.themaFiles || [];
	const mainCssFiles = mainThemeFiles.filter(f => f.themaCl === 'css');

	// 모든 CSS 파일 이름 수집
	const allCssFileNames = new Set();
	defaultCssFiles.forEach(f => allCssFileNames.add(f.fileNm));
	mainCssFiles.forEach(f => allCssFileNames.add(f.fileNm));

	// 각 CSS 파일 로드
	allCssFileNames.forEach(fileName => {
		// 메인 테마에 있는지 확인
		const mainFile = mainCssFiles.find(f => f.fileNm === fileName);
		const themeName = mainFile ? mainTheme.themaNmEng : (defaultTheme?.themaNmEng || 'default');

		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = `/client/views/pages/user/themes/${themeName}/resources/css/${fileName}`;
		link.setAttribute('data-theme-resource', 'css');
		link.onerror = () => {
			console.warn(`테마 CSS 로드 실패: ${fileName} (${themeName})`);
			if (themeName !== 'default') {
				const fallbackLink = document.createElement('link');
				fallbackLink.rel = 'stylesheet';
				fallbackLink.href = `/client/views/pages/user/themes/default/resources/css/${fileName}`;
				fallbackLink.setAttribute('data-theme-resource', 'css');
				fallbackLink.onerror = () => console.error(`Default CSS도 로드 실패: ${fileName}`);
				appendThemeCssLink(fallbackLink);
			}
		};
		appendThemeCssLink(link);
	});
}

// JS 동적 로드
function loadThemeJS(mainTheme, defaultTheme) {
	const mainThemeFiles = mainTheme?.themaFiles || [];
	const defaultThemeFiles = defaultTheme?.themaFiles || [];

	// 메인 테마 JS 파일
	const mainJsFiles = mainThemeFiles.filter(f => f.themaCl === 'js');

	if (mainJsFiles.length > 0) {
		mainJsFiles.forEach(file => {
			const script = document.createElement('script');
			script.src = `/client/views/pages/user/themes/${mainTheme.themaNmEng}/resources/js/${file.fileNm}`;
			script.setAttribute('data-theme-resource', 'js');
			script.onerror = () => console.warn(`테마 JS 로드 실패: ${file.fileNm}`);
			document.head.appendChild(script);
		});
	} else {
		// 메인 테마에 없으면 default 확인
		const defaultJsFiles = defaultThemeFiles.filter(f => f.themaCl === 'js');

		if (defaultJsFiles.length > 0) {
			defaultJsFiles.forEach(file => {
				const script = document.createElement('script');
				const themeName = defaultTheme?.themaNmEng || 'default';
				script.src = `/client/views/pages/user/themes/${themeName}/resources/js/${file.fileNm}`;
				script.setAttribute('data-theme-resource', 'js');
				script.onerror = () => console.warn(`Default JS 로드 실패: ${file.fileNm}`);
				document.head.appendChild(script);
			});
		}
	}
}

// Font 동적 로드 (@font-face 스타일 추가)
function loadThemeFonts(mainTheme, defaultTheme) {
	const mainThemeFiles = mainTheme?.themaFiles || [];
	const defaultThemeFiles = defaultTheme?.themaFiles || [];

	const fontFiles = mainThemeFiles.filter(f => f.themaCl === 'font');

	if (fontFiles.length === 0) {
		// 메인 테마에 없으면 default 확인
		const defaultFontFiles = defaultThemeFiles.filter(f => f.themaCl === 'font');
		if (defaultFontFiles.length > 0) {
			fontFiles.push(...defaultFontFiles.map(f => ({
				...f,
				themeName: defaultTheme?.themaNmEng || 'default'
			})));
		}
	} else {
		fontFiles.forEach(f => f.themeName = mainTheme.themaNmEng);
	}

	if (fontFiles.length > 0) {
		const style = document.createElement('style');
		style.setAttribute('data-theme-resource', 'font');

		let fontFaceCSS = '';
		fontFiles.forEach(file => {
			const fontName = file.fileNm.replace(/\.(ttf|otf|woff|woff2)$/i, '');
			const fontExt = file.fileNm.split('.').pop().toLowerCase();
			const fontFormat = fontExt === 'ttf' ? 'truetype' :
				fontExt === 'otf' ? 'opentype' :
					fontExt === 'woff' ? 'woff' : 'woff2';

			const themeName = file.themeName;

			fontFaceCSS += `
        @font-face {
          font-family: '${fontName}';
          src: url('/client/views/pages/user/themes/${themeName}/resources/font/${file.fileNm}') format('${fontFormat}');
          font-weight: normal;
          font-style: normal;
        }
      `;
		});

		style.textContent = fontFaceCSS;
		document.head.appendChild(style);
	}
}

// 테마 컴포넌트 헬퍼 함수
async function loadThemeComponent(componentName, category = 'bbs/common') {
	const themeInfo = getThemeInfo();
	const mainTheme = themeInfo?.mainTheme;
	const defaultTheme = themeInfo?.defaultTheme;

	const targetPath = `/${category}/${componentName}`;

	// 1. 메인 테마
	if (mainTheme?.themaFiles) {
		const hasInMainTheme = mainTheme.themaFiles.some(
			f => f.rltPath === targetPath
		);

		if (hasInMainTheme) {
			try {
				const module = await import(`/client/views/pages/user/themes/${mainTheme.themaNmEng}${targetPath}`);
				return module.default;
			} catch (err) {
				console.warn(`메인 테마 ${componentName} 로드 실패:`, err);
			}
		}
	}

	// 2. 기본 테마
	if (defaultTheme?.themaFiles) {
		const hasInDefaultTheme = defaultTheme.themaFiles.some(
			f => f.rltPath === targetPath
		);

		if (hasInDefaultTheme) {
			try {
				const defaultThemeName = defaultTheme.themaNmEng || 'default';
				const module = await import(`/client/views/pages/user/themes/${defaultThemeName}${targetPath}`);
				return module.default;
			} catch (err) {
				console.warn(`기본 테마 ${componentName} 로드 실패:`, err);
			}
		}
	}

	// 3. 공통 컴포넌트
	try {
		const name = componentName.replace('.vue', '');
		const module = await import(`/client/views/component/table/${name}.vue`);
		return module.default;
	} catch (err) {
		console.error(`${componentName}를 찾을 수 없습니다:`, err);
	}

	return null;
}

// 기존 테마 리소스 제거
function removeOldThemeResources() {
	const themeResources = document.querySelectorAll('[data-theme-resource]');
	themeResources.forEach(resource => resource.remove());
}

// 테마 API 조회 (캐시 활용)
async function fetchThemeData() {
	let defaultTheme = null;
	let mainTheme = null;

	// 기본 테마 조회
	try {
		const defaultRes = await findDefaultThema();
		if (defaultRes.status === 200 && defaultRes.data.data.thema) {
			defaultTheme = defaultRes.data.data.thema;
		}
	} catch (error) {
		console.warn('기본 테마 로드 실패:', error);
	}

	// 메인 테마 조회
	try {
		const mainRes = await findMainThema();
		if (mainRes.status === 200 && mainRes.data.data.thema) {
			mainTheme = mainRes.data.data.thema;
		}
	} catch (error) {
		console.warn('메인 테마 로드 실패:', error);
	}

	// 캐시에 저장
	themeApiCache = {
		defaultTheme,
		mainTheme,
		fetchedAt: Date.now()
	};

	return { defaultTheme, mainTheme };
}

// 메인 테마 조회 및 로드
async function loadMainTheme(ctx) {
	themeCssEnabled = !location.pathname.startsWith((ctx || '') + '/adm');

	removeOldThemeResources();

	// 테마 데이터 조회 (캐시됨)
	const { defaultTheme, mainTheme } = await fetchThemeData();

	if (!defaultTheme && !mainTheme) {
		// 둘 다 실패하면 하드코딩된 테마 로드
		loadHardcodedDefaultTheme();
		return {
			themeName: 'default',
			mainTheme: null,
			defaultTheme: null
		};
	}

	// 리소스 로드
	const themeName = mainTheme?.themaNmEng || defaultTheme?.themaNmEng || 'default';

	loadThemeCSS(mainTheme, defaultTheme);
	loadThemeJS(mainTheme, defaultTheme);
	loadThemeFonts(mainTheme, defaultTheme);

	const themeInfo = {
		themeName,
		mainTheme: mainTheme,
		defaultTheme: defaultTheme
	};

	globalThemeInfo = themeInfo;

	return themeInfo;
}

// 기본 테마 하드코딩 로드
function loadHardcodedDefaultTheme() {
	const defaultCssFiles = [
		'font.css',
		'reset.css',
		'layout.css',
		'style.css',
		'component.css',
		'variables.css',
		'board.css',
	];

	defaultCssFiles.forEach(fileName => {
		const link = document.createElement('link');
		link.rel = 'stylesheet';
		link.href = `/client/views/pages/user/themes/default/resources/css/${fileName}`;
		link.setAttribute('data-theme-resource', 'css');
		appendThemeCssLink(link);
	});
}

async function initVueApp() {
	// Store를 먼저 초기화 (API 호출 전)
	const store = createAppStore(null, null, null);

	// 이제 API 호출 가능 (Store가 초기화됨)
	const ctx = await contextPath();
	const strgMode = await storageMode();
	const lgnMode = await loginMode();

	// Store에 값 설정
	if (ctx) store.commit('setContextPath', ctx);
	if (lgnMode) store.commit('setLoginMode', lgnMode);

	// 테마 조회
	await loadMainTheme(ctx);

	const router = await createAppRouter();
	setGlobalRouter(router);

	const vue = createApp(App)
		.use(router)
		.use(store)
		.use(VueDaumPostcode)
		.use(COMMON_UTIL)
		.use(cmmnPlugin)
		.component("svg-icon", SvgIcon)
		.component('VueDatePicker', VueDatePicker);


	// 테마 이미지 URL 헬퍼 함수
	vue.config.globalProperties.getThemeImageUrl = function (imageName) {
		const themeInfo = getThemeInfo();
		const themeName = themeInfo?.themeName || 'default';

		const mainTheme = themeInfo?.mainTheme;
		if (mainTheme?.themaFiles) {
			const imageFile = mainTheme.themaFiles.find(f =>
				f.themaCl === 'img' && f.fileNm === imageName
			);

			if (imageFile) {
				return `/client/views/pages/user/themes/${themeName}${imageFile.rltPath}`;
			}
		}

		const defaultTheme = themeInfo?.defaultTheme;
		if (defaultTheme?.themaFiles) {
			const imageFile = defaultTheme.themaFiles.find(f =>
				f.themaCl === 'img' && f.fileNm === imageName
			);

			if (imageFile) {
				const defaultThemeName = defaultTheme.themaNmEng || 'default';
				return `/client/views/pages/user/themes/${defaultThemeName}${imageFile.rltPath}`;
			}
		}

		return `/client/views/pages/user/themes/default/resources/img/${imageName}`;
	};

	// 테마 컴포넌트 헬퍼 함수
	vue.config.globalProperties.loadThemeComponent = loadThemeComponent;

	// 권한 검증
	vue.config.globalProperties.hasAccessByType = function (type, rgtr) {
		const auth = this.$store.state.pageAuth;
		const userId = this.$store.state.mbrId;

		// 관리 권한 확인 시
		if (type === 'manage') {
			// 관리자(ROLE_ADMIN)는 페이지 권한과 무관하게 관리 권한 보유
			const roles = this.$store.state.roles;
			const isAdmin = Array.isArray(roles) && roles.some((r) => r.authority === 'ROLE_ADMIN');
			return isAdmin || (auth && auth.manageAuthrt === 'Y');
		}

		// 관리 권한 여부
		if (auth.manageAuthrt === 'Y') {
			return true;
		}

		// 본인 글 여부 체크 (rgtr가 유효하고 본인인 경우)
		const isOwner = rgtr && userId && rgtr === userId;

		// 일반 권한 확인
		switch (type) {
			// 등록 (쓰기)
			case 'create':
				return auth.regAuthrt === 'Y';

			// 읽기
			case 'read':
				// 읽기 권한이 있으면 전체 읽기 가능
				if (auth.inqAuthrt === 'Y') {
					return true;
				}
				// 쓰기 권한이 있고 본인 글이면 읽기 가능
				if (auth.regAuthrt === 'Y' && isOwner) {
					return true;
				}
				return false;

			// 수정
			case 'update':
				// 수정 권한이 있으면 전체 수정 가능
				if (auth.mdfcnAuthrt === 'Y') {
					return true;
				}
				// 쓰기 권한이 있고 본인 글이면 수정 가능
				if (auth.regAuthrt === 'Y' && isOwner) {
					return true;
				}
				return false;

			// 삭제
			case 'delete':
				// 삭제 권한이 있으면 전체 삭제 가능
				if (auth.delAuthrt === 'Y') {
					return true;
				}
				// 쓰기 권한이 있고 본인 글이면 삭제 가능
				if (auth.regAuthrt === 'Y' && isOwner) {
					return true;
				}
				return false;

			// 다운로드
			case 'download':
				// 다운로드 권한이 있으면 전체 다운로드 가능
				if (auth.fileDwnldAuthrt === 'Y') {
					return true;
				}
				// 쓰기 권한이 있고 본인 글이면 다운로드 가능
				if (auth.regAuthrt === 'Y' && isOwner) {
					return true;
				}
				return false;

			default:
				return false;
		}
	};

	vue.config.globalProperties.$filters = filters;
	vue.config.devtools = true;

	vue.use(VueCookies);
	vue.$cookies.config("1d");

	vue.mount("#root");
}

// Context Path 조회
async function contextPath() {
	try {
		const res = await getCntxtPth();
		if (res.status == 200) {
			return res.data.data;
		}
		return null;
	} catch (error) {
		console.log("Error fetching context path:", error);
		const errorData = error?.response?.data;
		if (errorData?.message) {
			alert(errorData.message);
		} else {
			alert("에러가 발생했습니다.\n관리자에게 문의해주세요.");
		}
		return null;
	}
};

// 스토리지 방식 조회
async function storageMode() {
	try {
		const res = await getStorageMode();
		if (res.status == 200) {
			return res.data.data;
		}
		return null;
	} catch (error) {
		const errorData = error?.response?.data;
		if (errorData?.message) {
			alert(errorData.message);
		} else {
			alert("에러가 발생했습니다.\n관리자에게 문의해주세요.");
		}
		return null;
	}
};

// 로그인 방식 조회
async function loginMode() {
	try {
		const res = await getLoginMode();
		if (res.status == 200) {
			return res.data.data;
		}
		return null;
	} catch (error) {
		const errorData = error?.response?.data;
		if (errorData?.message) {
			alert(errorData.message);
		} else {
			alert("에러가 발생했습니다.\n관리자에게 문의해주세요.");
		}
		return null;
	}
};

initVueApp();